import { useEffect, useState, useCallback, useRef } from 'react'
import './spinwheel.css'
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { useClient } from '../../context/ClientContext'
import { useLanguage } from '../../context/LanguageContext'
import { useTenant } from '../../context/TenantContext'
import { useGamification } from '../../context/GamificationContext'
import useNetworkStatus from './hooks/useNetworkStatus'
import Loader from '../../components/ui/loader'
import '../../utils/wheelSpin'
import wheelStandImage from '../../assets/images/games/wheel-stand.png'
import wheelRolling from '../../assets/music/wheel-rolling.mp3'
import congrats from '../../assets/music/congrats.mp3'

export default function SpinWheel() {
  const { isOnline } = useNetworkStatus()
  const { t, languageId, language } = useLanguage()
  const navigate = useNavigate()
  const { client, loading: clientLoading } = useClient()
  const { pickReward, issueReward, loading } = useGamification()
  const { tenant } = useTenant()
  const { tenanturlname } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { campaignId: campaignIdParam } = useParams()
  // Get campaignId from location state or URL query parameter
  const campaignIdFromState = location.state?.campaignId
  const campaignIdFromQuery = searchParams.get('campaignId')
  const campaignId =
    campaignIdParam || campaignIdFromState || campaignIdFromQuery

  const wheelAudioRef = useRef(null)
  const congratsAudioRef = useRef(null)

  const [isReady, setIsReady] = useState(true)
  const [rewardData, setRewardData] = useState(null)
  const [show, setShow] = useState(false)
  const [reward, setReward] = useState('')
  const [spining, setSpining] = useState(false)
  const [spinStarted, setSpinStarted] = useState(false)
  const [ambassador, setAmbassador] = useState('')
  const [loader, setLoader] = useState(true)

  const randomNumber = (min, max) => {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min)) + min
  }

  const fetchReward = useCallback(async () => {
    if (!client?.clientId || !campaignId) {
      setLoader(false)
      return null
    }
    setLoader(true)
    const decryptedData = await pickReward(client.clientId, campaignId)

    if (decryptedData) {
      setIsReady(false)
      setRewardData(decryptedData)
      setReward(parseInt(decryptedData.r))
      if (decryptedData.ri?.ambassadors?.length > 0) {
        setAmbassador(
          decryptedData.ri.ambassadors[
            randomNumber(0, decryptedData.ri.ambassadors.length)
          ],
        )
      }
      setLoader(false)

      return decryptedData
    }

    setLoader(false)
    return null
  }, [campaignId, client.clientId, pickReward])

  const reCheckReward = async () => {
    if (!client?.clientId || !campaignId) {
      return ''
    }
    const decryptedData = await pickReward(client.clientId, campaignId)
    if (decryptedData) {
      return decryptedData.r
    }
    return ''
  }

  useEffect(() => {
    if (campaignId && client?.clientId) {
      fetchReward()
    }
  }, [campaignId, client?.clientId, fetchReward])

  // Setup custom audio from reward data if available; otherwise use bundled wheel audio.
  useEffect(() => {
    if (wheelAudioRef.current) {
      wheelAudioRef.current.src = rewardData?.ri?.spinSoundUrl || wheelRolling
      wheelAudioRef.current.load()
    }
  }, [rewardData])

  const playWheelAudio = useCallback(() => {
    if (wheelAudioRef.current?.src) {
      try {
        wheelAudioRef.current.currentTime = 0
        wheelAudioRef.current.muted = false
        wheelAudioRef.current.volume = 1
        const playPromise = wheelAudioRef.current.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {})
        }
      } catch (_) {
        /* non-critical */
      }
    }
  }, [])

  const stopWheelAudio = useCallback(() => {
    if (wheelAudioRef.current) {
      wheelAudioRef.current.pause()
      wheelAudioRef.current.currentTime = 0
    }
  }, [])

  const getCongratsAudio = useCallback(() => {
    if (!congratsAudioRef.current && typeof Audio !== 'undefined') {
      congratsAudioRef.current = new Audio(congrats)
      congratsAudioRef.current.preload = 'auto'
    }

    return congratsAudioRef.current
  }, [])

  const unlockCongratsAudio = useCallback(() => {
    const audio = getCongratsAudio()
    if (!audio) return

    try {
      audio.currentTime = 0
      audio.volume = 0
      const playPromise = audio.play()
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            audio.pause()
            audio.currentTime = 0
            audio.volume = 1
          })
          .catch(() => {
            audio.volume = 1
          })
      } else {
        audio.pause()
        audio.currentTime = 0
        audio.volume = 1
      }
    } catch (_) {
      audio.volume = 1
    }
  }, [getCongratsAudio])

  const playCongratsAudio = useCallback(() => {
    const audio = getCongratsAudio()
    if (!audio) return

    try {
      audio.currentTime = 0
      audio.volume = 1
      const playPromise = audio.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } catch (_) {
      /* non-critical */
    }
  }, [getCongratsAudio])

  useEffect(() => {
    // Wait for client to finish loading before making redirect decisions
    if (clientLoading) return

    // Only redirect if user is not authenticated
    // If authenticated but no campaignId, allow page to load (will show error/loader)
    if (!client) {
      navigate(`/${tenanturlname}/signin`)
      return
    }

    // If authenticated but no campaignId, don't redirect - let the page handle it
    // The fetchReward will fail gracefully if campaignId is missing
  }, [campaignId, client, clientLoading, navigate, tenanturlname])

  useEffect(() => {
    if (isOnline) setShow(false)
    else setShow(true)
  }, [isOnline])

  const handleIssueReward = async () => {
    await issueReward(client?.clientId, {
      campaignId: campaignId,
      transactionId: rewardData.ti,
      rewardAmount: reward,
      action: 'Spin the Wheel',
    })
  }

  const onSpin = async () => {
    setSpinStarted(true)

    // Track spin wheel CTA click for GTM
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'cta_click', ctaName: 'spin_wheel' })
    }

    // Mobile browsers require audio to start directly from the user gesture.
    playWheelAudio()
    unlockCongratsAudio()

    const decryptedReward = await reCheckReward()
    if (decryptedReward !== '') {
      setSpining(true)

      // Note: window.spin() function should be defined by the spin wheel library
      if (window.spin && rewardData?.p) {
        // Parse the angle range from rewardData.p (format: "start-end")
        const angleRange = rewardData.p.split('-')
        const startAngle = parseInt(angleRange[0].trim())
        const endAngle = parseInt(angleRange[1].trim())

        // Calculate a random angle within the range (with small margin for safety)
        // Use the middle of the range for more accurate stopping
        const rangeSize = endAngle - startAngle
        const margin = Math.min(5, rangeSize / 4) // Use 5 degrees or 1/4 of range, whichever is smaller
        const angle = randomNumber(
          Math.floor(startAngle + margin),
          Math.ceil(endAngle - margin),
        )

        window.spin(angle)
      }
      handleIssueReward()
      setTimeout(() => {
        setSpining(false)
      }, 2500)
      setTimeout(() => {
        stopWheelAudio()

        playCongratsAudio()

        navigate(`/${tenanturlname}/gamification/congratulation`, {
          state: {
            reward: reward,
            chances: rewardData.asc,
            campaignId: campaignId,
            congratsImage: rewardData.ri?.congratulation,
            imageUrl: rewardData.ri?.[`social-media-${reward}`][languageId],
            tnxId: rewardData.ti,
            campaignType: 'SPIN',
            campaignConfig: rewardData.ri || {},
            winAnimations: rewardData.ri?.['win-animations'] || [],
          },
        })
      }, 9000)
    } else {
      stopWheelAudio()
    }
  }

  if ((loader || loading) && !spinStarted) {
    return (
      <Loader
        fullscreen
        label={t('homePage.loading')}
        size="xl"
      />
    )
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      {show && (
        <div style={{ margin: '10px 50px' }}>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong>
              {t('common.networkError') || 'Network Connection Error'}
            </strong>
            <p>
              {t('common.networkErrorDesc') ||
                'Please check your internet connection.'}
            </p>
            <button onClick={() => setShow(false)} className="mt-2">
              ×
            </button>
          </div>
        </div>
      )}
      <br />
      <div
        className="align-center lg:text-2xl text-lg"
        style={{ color: '#1434CB', fontWeight: 'bold', padding: '0 10px' }}
      >
        <h3 style={{ margin: 0 }}>
          {t('gamification.wheelSpinCampaign') || 'Wheel Spin Campaign'}
        </h3>
      </div>
      <div
        style={{ padding: '0 10px' }}
        className="align-center gray-text text-xs lg:text-lg "
      >
        <span>
          {t('gamification.wheelSpinDesc1') ||
            'Spin the wheel and win rewards!'}{' '}
          <br />
          {t('gamification.wheelSpinDesc2') || 'Try your luck today!'}
        </span>
      </div>
      <div className="wrapper align-center">
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        >
          {ambassador && (
            <img className="sw-header-img" src={ambassador} alt="ambassador" />
          )}
        </div>
        <div
          style={{
            marginTop: '-400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <div className="pointer-wrapper">
            {rewardData?.ri?.pointer && (
              <img
                width={40}
                className={spining ? 'vibrate' : ''}
                src={rewardData.ri.pointer}
                alt="pointer"
                style={{ display: 'block' }}
              />
            )}
          </div>
          {rewardData?.ri?.spinwheel && (
            <img
              width={300}
              height={300}
              src={rewardData.ri.spinwheel}
              alt="spinning wheel"
              className="wheel"
              style={{ display: 'block', margin: '0 auto' }}
            />
          )}
        </div>
      </div>
      <div style={{ marginTop: '5px' }} className="align-center">
        <img
          width={100}
          src={wheelStandImage}
          alt="wheel stand"
          style={{ display: 'block', margin: '0 auto' }}
          onError={(e) => {
            e.target.style.display = 'none'
          }}
        />
      </div>
      <br />
      <div
        className="align-center"
        style={{ fontSize: '16px', fontWeight: 'bold' }}
      >
        <span>
          {t('gamification.youCanSpinFor') || 'You can spin the wheel for'}
        </span>
        <span style={{ color: '#1434CB', fontSize: '30px' }}>
          {' '}
          {rewardData?.asc || 0}{' '}
        </span>
        <span> {t('gamification.times') || 'times'}!</span>
      </div>
      <br />
      <div className="align-center">
        {rewardData?.asc < 1 || spinStarted ? (
          <button
            style={{
              paddingLeft: '10px',
              backgroundColor: '#cccccc',
              color: '#666666',
              width: '200px',
              height: '40px',
              borderRadius: '30px',
              border: 'none',
              fontSize: '17px',
              cursor: 'not-allowed',
            }}
            className="btn-bg"
            disabled
          >
            <span style={{ textAlign: 'center' }}>
              {t('gamification.spinTheWheel') || 'Spin the Wheel'}
            </span>
          </button>
        ) : (
          <button
            disabled={isReady}
            onClick={() => (isOnline ? onSpin() : null)}
            style={{
              paddingLeft: '10px',
              backgroundColor: isReady ? '' : '#1434CB',
              color: isReady ? 'black' : 'white',
              width: '200px',
              height: '40px',
              borderRadius: '30px',
              border: 'none',
              fontSize: '17px',
              cursor: isReady ? 'not-allowed' : 'pointer',
            }}
            className="btn-bg"
          >
            <span style={{ textAlign: 'center' }}>
              {t('gamification.spinTheWheel') || 'Spin the Wheel'}
            </span>
          </button>
        )}
      </div>
      <br />
      {rewardData?.ri?.campaignDetailsText && (
        <div className="align-center" style={{ margin: '0px 10px' }}>
          <span>
            {t('gamification.campaignDetailsText') || 'For campaign details, '}
            <a
              href={rewardData.ri.campaignDetailsText}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 'bold', color: '#1434CB' }}
            >
              {t('gamification.clickHere') || 'click here'}
            </a>
          </span>
        </div>
      )}
      <br />
      {rewardData?.ri?.tnc && (
        <div style={{ margin: '0px 10px' }} className="align-center">
          {language?.languagecode === 'ja' ? (
            <span>
              {rewardData.ri.tnc[language?.languagecode]?.text}{' '}
              <a
                href={rewardData.ri.tnc[language?.languagecode]?.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1434CB' }}
              >
                {rewardData.ri.tnc[language?.languagecode]?.['link-text'] || ''}
              </a>
            </span>
          ) : (
            <span>
              <a
                href={rewardData.ri.tnc[language?.languagecode]?.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1434CB' }}
              >
                {rewardData.ri.tnc[language?.languagecode]?.['link-text'] || ''}
              </a>{' '}
              {rewardData.ri.tnc[language?.languagecode]?.text}
            </span>
          )}
        </div>
      )}
      <br />

      {/* Audio element for wheel sound. Native audio is more reliable on mobile than WebAudio. */}
      <audio ref={wheelAudioRef} src={wheelRolling} preload="auto" />
    </div>
  )
}
